import { useCallback, useEffect, useMemo, useState } from 'react'
import tenantApi from '@/api/tenant/TenantApi'
import { GetReferralStatsRes } from '@/api/tenant/models/GetReferralStatsRes'
import Button from '@/uiV2/common/form/inputs/Button'
import Note from '@/components/Note'
import { NoteType } from '@/enums/NoteType'

type ReferralsTabProps = {
    isActive: boolean
}

const QUICK_RANGES = [
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
]

const formatDateInput = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const getDefaultStartDate = () => {
    const today = new Date()
    const start = new Date(today)
    start.setDate(start.getDate() - 6)
    return formatDateInput(start)
}

const getDefaultEndDate = () => {
    return formatDateInput(new Date())
}

const formatPercent = (value: number): string => `${(value * 100).toFixed(1)}%`

const formatDecimal = (value: number): string => value.toFixed(3)

function ReferralsTab({ isActive }: ReferralsTabProps): React.JSX.Element {
    const [startDate, setStartDate] = useState<string>(() =>
        getDefaultStartDate(),
    )
    const [endDate, setEndDate] = useState<string>(() => getDefaultEndDate())
    const [stats, setStats] = useState<GetReferralStatsRes | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const numberFormatter = useMemo(() => new Intl.NumberFormat('en-US'), [])

    const buildRange = useCallback(() => {
        if (!startDate || !endDate) {
            throw new Error('Both start and end dates are required')
        }

        const startDt = new Date(`${startDate}T00:00:00Z`)
        const endDt = new Date(`${endDate}T00:00:00Z`)

        if (Number.isNaN(startDt.getTime())) {
            throw new Error('Invalid start date')
        }
        if (Number.isNaN(endDt.getTime())) {
            throw new Error('Invalid end date')
        }

        if (endDt.getTime() < startDt.getTime()) {
            throw new Error('End date must be on or after start date')
        }

        const endExclusive = new Date(endDt)
        endExclusive.setUTCDate(endExclusive.getUTCDate() + 1)

        return {
            start: startDt.toISOString(),
            end: endExclusive.toISOString(),
        }
    }, [startDate, endDate])

    const fetchStats = useCallback(async () => {
        try {
            setIsLoading(true)
            setError(null)
            const range = buildRange()
            const data = await tenantApi.getReferralStats(range)
            setStats(data)
        } catch (err) {
            console.error('Failed to load referral stats', err)
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to load referral stats',
            )
        } finally {
            setIsLoading(false)
        }
    }, [buildRange])

    useEffect(() => {
        if (isActive && !stats && !isLoading) {
            void fetchStats()
        }
    }, [fetchStats, isActive, isLoading, stats])

    const applyQuickRange = (days: number) => {
        const today = new Date()
        const end = formatDateInput(today)
        const start = new Date(today)
        start.setDate(start.getDate() - (days - 1))
        setStartDate(formatDateInput(start))
        setEndDate(end)
    }

    const statCards = stats ? (
        <div className="grid gap-4 md:grid-cols-3">
            <StatCard
                title="K (proxy)"
                value={formatDecimal(stats.kReferralProxy)}
                description={`${numberFormatter.format(
                    stats.referredSignupsInWindow,
                )} referred signups / ${numberFormatter.format(
                    stats.existingUsersAtStart,
                )} existing users`}
                note="Calculated as referred signups that happened inside this window divided by users who existed before the window began. If it’s above 1, referrals on their own are making the base grow."
            />
            <StatCard
                title="Referral share"
                value={formatPercent(stats.referralShare)}
                description={`${numberFormatter.format(
                    stats.referredSignupsInWindow,
                )} referred signups / ${numberFormatter.format(
                    stats.totalSignupsInWindow,
                )} total signups`}
                note="Shows what share of all signups inside this window came from referrals versus other channels during that same window."
            />
            <StatCard
                title="Referrals per referrer"
                value={formatDecimal(stats.referralsPerReferrer)}
                description={`${numberFormatter.format(
                    stats.referredSignupsInWindow,
                )} referred signups / ${numberFormatter.format(
                    stats.referrersInWindow,
                )} distinct referrers`}
                note="Represents how many referred signups happened per referrer, counting only referral activity inside this window."
            />
        </div>
    ) : null

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 rounded-lg bg-white p-4 shadow">
                <div>
                    <h3 className="page-title mb-1">Referrals</h3>
                </div>
                <div className="flex flex-wrap gap-4">
                    <DateField
                        label="Start date"
                        value={startDate}
                        onChange={setStartDate}
                    />
                    <DateField
                        label="End date"
                        value={endDate}
                        onChange={setEndDate}
                    />
                    <div className="flex flex-col gap-2">
                        <span className="text-xs uppercase tracking-wide text-gray-500">
                            Quick ranges
                        </span>
                        <div className="flex gap-2">
                            {QUICK_RANGES.map((range) => (
                                <button
                                    key={range.label}
                                    type="button"
                                    className="rounded-full border border-gray-300 px-3 py-1 text-xs uppercase tracking-wide text-gray-600 transition hover:border-ctxt-blue hover:text-ctxt-blue"
                                    onClick={() => applyQuickRange(range.days)}
                                >
                                    {range.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="ml-auto">
                        <Button
                            type="button"
                            style="primary"
                            enabled={!isLoading}
                            onClick={() => {
                                void fetchStats()
                            }}
                        >
                            {isLoading ? 'Loading…' : 'Refresh stats'}
                        </Button>
                    </div>
                </div>
                {error && (
                    <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-600">
                        {error}
                    </div>
                )}
            </div>
            {statCards}
            {stats && (
                <div className="rounded-lg bg-white p-4 shadow">
                    <h4 className="mb-3 text-base font-semibold text-gray-800">
                        Window details
                    </h4>
                    <dl className="grid gap-4 text-sm md:grid-cols-2">
                        <DataRow
                            label="Window start (UTC)"
                            value={new Date(stats.start).toUTCString()}
                        />
                        <DataRow
                            label="Window end (UTC, exclusive)"
                            value={new Date(stats.end).toUTCString()}
                        />
                        <DataRow
                            label="Existing users at start"
                            value={numberFormatter.format(
                                stats.existingUsersAtStart,
                            )}
                        />
                        <DataRow
                            label="Total signups in window"
                            value={numberFormatter.format(
                                stats.totalSignupsInWindow,
                            )}
                        />
                        <DataRow
                            label="Referred signups"
                            value={numberFormatter.format(
                                stats.referredSignupsInWindow,
                            )}
                        />
                        <DataRow
                            label="Users who referred in window"
                            value={numberFormatter.format(
                                stats.referrersInWindow,
                            )}
                        />
                    </dl>
                </div>
            )}
        </div>
    )
}

type DateFieldProps = {
    label: string
    value: string
    onChange: (value: string) => void
}

const DateField = ({ label, value, onChange }: DateFieldProps) => (
    <label className="flex flex-col gap-1 text-sm text-gray-700">
        <span className="text-xs uppercase tracking-wide text-gray-500">
            {label}
        </span>
        <input
            type="date"
            className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-ctxt-blue focus:outline-none"
            value={value}
            max={getDefaultEndDate()}
            onChange={(event) => onChange(event.target.value)}
        />
    </label>
)

type StatCardProps = {
    title: string
    value: string
    description: string
    note: string
}

const StatCard = ({ title, value, description, note }: StatCardProps) => (
    <div className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
        <div className="text-xs uppercase tracking-wide text-gray-500">
            {title}
        </div>
        <div className="mt-2 text-3xl font-semibold text-gray-900">{value}</div>
        <div className="mt-1 text-xs text-gray-500">{description}</div>
        <div className="mt-3">
            <Note type={NoteType.INFO}>{note}</Note>
        </div>
    </div>
)

type DataRowProps = {
    label: string
    value: string
}

const DataRow = ({ label, value }: DataRowProps) => (
    <div className="flex flex-col">
        <span className="text-xs uppercase tracking-wide text-gray-500">
            {label}
        </span>
        <span className="text-sm text-gray-900">{value}</span>
    </div>
)

export default ReferralsTab
